import { useState, useEffect, useRef } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';

interface DeleteProjectDialogProps {
  isOpen: boolean;
  projectName: string;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  error?: Error | null;
}

/**
 * Confirmation dialog for deleting a project
 * Requires user to type the project name to confirm
 */
export function DeleteProjectDialog({
  isOpen,
  projectName,
  onClose,
  onConfirm,
  isLoading = false,
  error,
}: DeleteProjectDialogProps) {
  const [confirmText, setConfirmText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isConfirmed = confirmText === projectName;

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && !isLoading) {
        onClose();
      }
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, onClose]);

  // Reset form when dialog closes
  const handleClose = () => {
    onClose();
    // Reset form after close animation
    setTimeout(() => {
      setConfirmText('');
    }, 150);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isConfirmed || isLoading) return;
    onConfirm();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={isLoading ? undefined : handleClose}
      />

      {/* Modal */}
      <div className="relative bg-card rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden border border-border">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-destructive/10 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">
              Delete Project
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-5">
            {/* Error message */}
            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
                {error.message || 'Failed to delete project. Please try again.'}
              </div>
            )}

            {/* Warning */}
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg space-y-3">
              <p className="text-sm font-medium text-destructive">
                This action is permanent and cannot be undone.
              </p>
              <p className="text-sm text-muted-foreground">
                Deleting <span className="font-mono font-medium text-foreground">{projectName}</span> will remove:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                <li>All environments and their secrets</li>
                <li>All API tokens for this project</li>
                <li>Team member access and permissions</li>
                <li>Audit logs and version history</li>
              </ul>
            </div>

            {/* Confirmation input */}
            <div className="space-y-3">
              <label
                htmlFor="confirm-project-name"
                className="block text-sm text-muted-foreground"
              >
                To confirm, type the project name below:
              </label>
              <div className="font-mono text-sm bg-muted px-3 py-2 rounded-lg text-foreground select-all">
                {projectName}
              </div>
              <input
                ref={inputRef}
                type="text"
                id="confirm-project-name"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type project name here"
                className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-destructive/50 focus:border-destructive/50 transition-colors font-mono"
                disabled={isLoading}
                autoComplete="off"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-muted/30 border-t border-border flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isConfirmed || isLoading}
              className="px-4 py-2 text-sm font-medium text-destructive-foreground bg-destructive rounded-lg hover:bg-destructive/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Project'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
